/**
 * Chatbot API Service
 * Handles real API communication with the chatbot backend using NewConversationService
 */

import { ResponseStage, ResponseProgress, StructuredContent } from '@stores/chatbot/types';
import NewConversationService, { CreateNewConversationRequest } from '@apis/NewConversationService';
import { useChatbotStore } from '@stores/chatbot/chatbotStore';
import { buildMessageWithAttachments } from '@services/chatbot/buildMessageWithAttachments';
import { extractExecutionTodoFromStreamChunk } from '@services/chatbot/executionTodoState';
import { addToolBreadcrumb, captureChatWarning } from '@libs/sentryChat';
export interface ChatbotApiResponse {
  id: string;
  content: string | StructuredContent; // Support both string and structured content
  timestamp: number;
  conversationId?: string;
  suggestedResponses?: string[]; // Suggested follow-up responses from the agent
  metadata?: {
    model?: string;
    requires_approval?: boolean;
    requires_questionnaire?: boolean;
    questionnaire_data?: {
      questions: Array<{ id: string; prompt: string; options: string[]; allow_freeform: boolean }>;
      conversation_id: string;
    };
    status?: string;
    usage?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    isContinuation?: boolean; // Flag to indicate this is a continuation response
    isInterrupted?: boolean;
    error?: string;
    isRetryable?: boolean;
  };
}

export interface ChatbotApiError {
  error: string;
  message: string;
  code?: string;
}

export type ProgressCallback = (progress: ResponseProgress) => void;

type ToolMarker = { position: number; tool: string; runId?: string; message?: string; success?: boolean | null };

const finalizeInProgressToolMarkers = (toolMarkers: ToolMarker[]): void => {
  for (let i = 0; i < toolMarkers.length; i++) {
    const marker = toolMarkers[i];
    if (marker.success === null || marker.success === undefined) {
      toolMarkers[i] = { ...marker, success: false };
    }
  }
};

const isStoppedByUserStreamInterruption = (chunk: any): boolean => {
  const err = String(chunk?.error || '').toLowerCase();
  return err === 'stopped by user' || err.includes('stopped by user');
};

class ChatbotApiService {
  private abortController: AbortController | null = null;

  /**
   * Send a message to the chatbot and get a response using existing ConversationService
   */
  async sendMessage(
    message: string,
    conversationId?: string,
    onProgress?: ProgressCallback,
    uploadedImages?: any[]
  ): Promise<ChatbotApiResponse> {
    const startTime = Date.now();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    let progressInterval: number | null = null;
    
    // Declare variables at function scope to be accessible in catch block
    let streamingResponse = '';
    let finalConversationId: string | undefined;
    let finalMetadata: any = {};

    try {
      // Start waiting stage
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 25,
        currentActivity: 'Leo is thinking...',
        startTime,
        estimatedDuration: 30000,
        canCancel: true,
      });

      const messageString = buildMessageWithAttachments(message, uploadedImages);

      // Prepare request payload
      const requestPayload: CreateNewConversationRequest = {
        message: messageString,
        conversation_id: conversationId,
        enable_streaming: false,
        uploaded_images: uploadedImages?.map(im => ({
          s3_url: im.s3_url,
          asset_id: im.asset_id,
          description: im.description,
        })),
      };

      // Use NewConversationService with status code
      const { status, data } = await NewConversationService.create(requestPayload);
      if (progressInterval) clearInterval(progressInterval);
      const responseData = data as any;
      const responsePayload = responseData?.response;
      finalConversationId = responseData?.conversation_id || responsePayload?.conversation_id || conversationId;
      finalMetadata = responseData || {};

      console.log('📊 [chatbotApi.sendMessage] Final streaming state:', {
        streamingResponseLength: streamingResponse.length,
        hasResponse: !!streamingResponse?.trim(),
        responsePreview: streamingResponse?.slice(0, 100) + (streamingResponse?.length > 100 ? '...' : ''),
        finalConversationId,
        status
      });

      // Streaming response completed

      // Check for approval status in final metadata
      if (status === 202 || responsePayload?.status === 'pending_approval') {
        const r202 = responsePayload || finalMetadata;
        
        onProgress?.({
          requestId,
          stage: ResponseStage.IDLE,
          progress: 100,
          currentActivity: 'Approval required',
          startTime,
          estimatedDuration: 0,
          canCancel: false,
        });

        // Return structured content with approval data
        return {
          id: finalConversationId ?? `response_${Date.now()}`,
          content: {
            requires_approval: true,
            response: r202?.message || streamingResponse || "I need your approval to execute the following action(s):",
            approval_data: r202?.approval_data,
            request_type: 'approval',
            request_text: '',
          },
          timestamp: Date.now(),
          conversationId: finalConversationId,
          metadata: {
            requires_approval: true,
            status: 'pending_approval',
          },
        };
      }

      // Handle normal streaming response
      // We already have the complete response from streaming
      let assistantResponse = streamingResponse;

      if (!assistantResponse || assistantResponse.trim() === '') {
        console.error('❌ chatbotApi.sendMessage - No streaming response received', {
          streamingResponseLength: streamingResponse?.length || 0,
          finalMetadata,
          status
        });
        
        // Check if we have metadata with response content
        if (typeof responsePayload === 'string') {
          assistantResponse = responsePayload;
        } else if (typeof responsePayload?.message === 'string') {
          assistantResponse = responsePayload.message;
        } else if (typeof responsePayload?.response === 'string') {
          assistantResponse = responsePayload.response;
        } else if (finalMetadata?.response && typeof finalMetadata.response === 'string') {
          console.log('📋 [chatbotApi.sendMessage] Using response from finalMetadata', {
            metadataResponseLength: finalMetadata.response.length,
            metadataResponsePreview: finalMetadata.response.slice(0, 100) + '...'
          });
          assistantResponse = finalMetadata.response;
        } else {
          console.error('❌ [chatbotApi.sendMessage] No response in metadata either', {
            hasFinalMetadata: !!finalMetadata,
            finalMetadataKeys: finalMetadata ? Object.keys(finalMetadata) : [],
            finalMetadataResponse: finalMetadata?.response,
            finalMetadataResponseType: typeof finalMetadata?.response
          });
          throw new Error('No assistant response received from streaming request');
        }
      }

      onProgress?.({
        requestId,
        stage: ResponseStage.IDLE,
        progress: 100,
        currentActivity: 'Response complete',
        startTime,
        estimatedDuration: 0,
        canCancel: false,
      });

      return {
        id: finalConversationId ?? `response_${Date.now()}`,
        content: assistantResponse,
        timestamp: Date.now(),
        conversationId: finalConversationId,
        metadata: {
          requires_approval: finalMetadata?.requires_approval,
          ...finalMetadata,
        },
      };
    } catch (error) {
      // Make sure to clear the progress interval on error
      if (progressInterval) clearInterval(progressInterval);
      
      console.error('❌ [chatbotApi.sendMessage] Error caught:', {
        error,
        errorType: error?.constructor?.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        streamingResponseLength: streamingResponse?.length || 0,
        finalConversationId,
        timestamp: new Date().toISOString()
      });
      
      // Provide better error context
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request cancelled');
        }
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * Send message with streaming (SSE)
   * Supports SSE reconnection via Last-Event-ID header for resilient streaming.
   */
  async sendMessageStream(
    message: string,
    conversationId: string,
    uploadedImages?: any[],
    isRetry?: boolean,
    lastEventIdOverride?: number,
    initialContent?: string,
    isHidden?: boolean,
    onProgress?: ProgressCallback,
    displayText?: string
  ): Promise<ChatbotApiResponse> {
      const startTime = Date.now();
      const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      let fullText = initialContent || '';
      let outputTokenCount = 0; // Track output tokens (from streaming)
      let baselineInputTokens = 0; // System prompt + tools + user message + history
      let metaToolsTokens = 0; // Meta tools when loaded
      let toolResponseTokens = 0; // Cumulative tool response tokens
      const activeTools = new Set<string>();
      const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];
      let toolsNodeCompleted = false;
      let finalResponseStarted = false;
      
      // Track last event ID for SSE reconnection support
      // If connection drops, this can be used to resume from the last received event
      let lastEventId = lastEventIdOverride || 0;

      // Create AbortController for cancellation support
      this.abortController = new AbortController();

    try {
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 25,
        currentActivity: 'Starting stream...',
        startTime,
        estimatedDuration: 30000,
        canCancel: true,
      });
      
      if (fullText) {
        onProgress?.({
          requestId,
          stage: ResponseStage.WAITING,
          progress: 25,
          currentActivity: 'Resuming...',
          startTime,
          estimatedDuration: 30000,
          canCancel: true,
          streamedContent: fullText,
        });
      }


      // Format message with images if provided (same format as sendMessage)
      const messageString = buildMessageWithAttachments(message, uploadedImages);

      // SSE reconnection configuration
      const MAX_RECONNECT_WINDOW_MS = 15000; // Silent retry window for brief disconnects
      const MAX_RECONNECT_ATTEMPTS = 10;
      const RECONNECT_DELAY_MS = 1000;
      const reconnectStart = Date.now();
      let reconnectAttempts = 0;
      let hadNetworkDisconnect = false;
      let streamComplete = false;
      
      // Reconnection loop - will retry on network errors
      while (!streamComplete && reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
        try {
          // Stream using NewConversationService
          // The service now yields { eventId, data } for SSE reconnection support
          // On reconnection, lastEventId tells the backend to skip already-received events
          for await (const { eventId, data: chunk } of NewConversationService.streamMessage(
            conversationId,
            messageString,
            uploadedImages,
            isRetry,
            lastEventId > 0 ? lastEventId : undefined,  // Only send on reconnection
            isHidden,
            this.abortController?.signal,
            displayText
          )) {
            // Track the last event ID for potential reconnection
            if (eventId > lastEventId) {
              lastEventId = eventId;
              const store = useChatbotStore.getState();
              if (store.conversationId === conversationId) {
                store.updateSSEConnectionState({ lastEventId });
              }
            }
            
            // Reset reconnect attempts on successful data receipt
            reconnectAttempts = 0;
            if (hadNetworkDisconnect) {
              const store = useChatbotStore.getState();
              if (store.conversationId === conversationId) {
                store.handleSSEReconnect();
              }
              hadNetworkDisconnect = false;
            }
            

        if (chunk.type === 'keepalive') {
          // Keepalive comments are normal heartbeats; avoid false "reconnecting" UI.
          continue;
        } else if (chunk.type === 'capacity_info') {
          // Update capacity from backend (single source of truth)
          // Backend sends one capacity_info event per exchange, after messages are saved to S3
          const tokenCount = chunk.token_count || 0;
          const maxTokens = chunk.max_tokens || 200000;
          const capacityPercentage = chunk.capacity_percentage || 0;
          
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,  // Capacity arrives after save, so we're done
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
            messageCapacity: {
              tokenCount,
              capacityPercentage,
              maxTokens,
              isAtLimit: capacityPercentage >= 100
            }
          } as any);
        } else if (chunk.type === 'token_baseline') {
          // Capture baseline input tokens (system prompt, tools, user message, history)
          baselineInputTokens = chunk.input_tokens || 0;
        } else if (chunk.type === 'meta_tools_loaded') {
          // Add Meta tools tokens when platform is activated
          metaToolsTokens = chunk.meta_tools_tokens || 0;
        } else if (chunk.type === 'token') {
          fullText += chunk.content || '';
          outputTokenCount++;
          
          // If tokens are streaming after tools completed, mark incomplete tools as complete
          if (toolsNodeCompleted && !finalResponseStarted) {
            finalResponseStarted = true;
            toolMarkers.forEach((marker, index) => {
              if (!marker.message) {
                toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
              } else if (marker.success === null || marker.success === undefined) {
                toolMarkers[index] = { ...marker, success: true };
              }
            });
          }
          
          // Calculate total tokens (with better approximation)
          // Output tokens: use 1.5x coefficient since chunks aren't exactly tokens
          // Input tokens: baseline + meta tools + tool responses
          const estimatedOutputTokens = Math.round(outputTokenCount * 1.5);
          const totalTokens = baselineInputTokens + metaToolsTokens + toolResponseTokens + estimatedOutputTokens;
          
          onProgress?.({
            requestId,
            stage: ResponseStage.STREAMING,
            progress: 75,
            currentActivity: `Streaming... (${totalTokens.toLocaleString()} tokens)`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
          });
        } else if (chunk.type === 'tool_start') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `using ${toolName}`;
          const runId = chunk.run_id;
          const toolInput = chunk.input || {};
          activeTools.add(toolName);
          addToolBreadcrumb(toolName, 'start', { runId });
          
          // Track tool inputs for use in tool_end events
          if (
            runId &&
            (toolName === 'update_draft' ||
              toolName === 'publish_report' ||
              toolName === 'set_report_metadata' ||
              toolName === 'update_report' || // legacy, kept for in-flight conversations
              toolName === 'create_report' ||
              toolName === 'update_point_explanation')
          ) {
            (window as unknown as Record<string, unknown>).__pendingToolInputs =
              (window as unknown as Record<string, Record<string, unknown>>).__pendingToolInputs || {};
            (window as unknown as Record<string, Record<string, unknown>>).__pendingToolInputs[runId] = toolInput;
          }
          
          // Record tool marker at current text position with message
          // Use position in text, or 0 if no text yet (tools called before response)
          const position = fullText.length;
          const marker = { position, tool: toolName, runId, message: toolMessage, success: null }; // null = in progress
          
          // Handle tool marker creation/update based on runId
          if (runId) {
            // If runId provided, check for existing marker to update (e.g., video status progression)
            const existingIndex = toolMarkers.findIndex(m => m.runId === runId);
            if (existingIndex !== -1) {
              // Update existing marker's message for progressive status updates
              toolMarkers[existingIndex] = { ...toolMarkers[existingIndex], message: toolMessage };
            } else {
              // New marker with this runId
              toolMarkers.push(marker);
            }
          } else {
            // No runId - use position+tool+message for deduplication (legacy behavior)
            const exists = toolMarkers.some(m => m.position === position && m.tool === toolName && m.message === toolMessage);
            if (!exists) {
              toolMarkers.push(marker);
            }
          }
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 60,
            currentActivity: `Using tool: ${toolName}`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
          });
        } else if (chunk.type === 'tool_end') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `Executed ${toolName}`;
          // Set success: true if explicitly true, false if explicitly false, otherwise null (in progress)
          const toolSuccess = chunk.success === true ? true : chunk.success === false ? false : null;
          const runId = chunk.run_id;
          const executionTodo = extractExecutionTodoFromStreamChunk(chunk);
          activeTools.delete(toolName);
          addToolBreadcrumb(toolName, 'end', { runId, success: toolSuccess });

          if (executionTodo) {
            const store = useChatbotStore.getState();
            if (store.conversationId === conversationId) {
              store.setExecutionTodo(executionTodo);
            }
          }
          
          // Emit custom event when report is updated/created/published so Leo Reports page can auto-refresh
          if (
            (toolName === 'update_draft' ||
              toolName === 'publish_report' ||
              toolName === 'set_report_metadata' ||
              toolName === 'update_report' || // legacy
              toolName === 'create_report') &&
            toolSuccess !== false
          ) {
            // Try to get report_id from cached tool input or parse from message
            let reportId: string | undefined;
            const pendingInputs = (window as unknown as Record<string, Record<string, Record<string, unknown>>>).__pendingToolInputs;

            // For update_draft / publish_report etc., get report_id from cached input
            if (runId && pendingInputs?.[runId]) {
              reportId = pendingInputs[runId]?.report_id as string | undefined;
              delete pendingInputs[runId];
            }
            
            // For create_report (or if report_id wasn't in input), parse from success message
            if (!reportId && toolMessage) {
              const match = toolMessage.match(/report_\d+_[a-f0-9]+/);
              if (match) {
                reportId = match[0];
              }
            }
            
            console.log(`📊 SSE: ${toolName} completed, reportId=${reportId}`);
            window.dispatchEvent(new CustomEvent('leo-report-updated', { 
              detail: { reportId, action: toolName === 'create_report' ? 'created' : 'updated' }
            }));
          }
          
          // Emit custom event when strategic notes are updated so My Company page can auto-refresh
          if (toolName === 'update_strategic_notes' && toolSuccess !== false) {
            window.dispatchEvent(new CustomEvent('leo-strategic-notes-updated'));
          }

          // Emit rundown point event so the chart can highlight + typewriter the explanation
          if (toolName === 'update_point_explanation' && toolSuccess !== false) {
            const pendingRundownInputs = (window as unknown as Record<string, Record<string, Record<string, unknown>>>).__pendingToolInputs;
            if (runId && pendingRundownInputs?.[runId]) {
              const input = pendingRundownInputs[runId];
              window.dispatchEvent(new CustomEvent('leo-rundown-point', {
                detail: {
                  objectiveId: input.objective_id,
                  date: input.date,
                  narrative: input.explanation,
                },
              }));
              delete pendingRundownInputs[runId];
            }
          }
          
          // Add tool response tokens to cumulative total
          if (chunk.response_tokens) {
            toolResponseTokens += chunk.response_tokens;
          }
          
          // Update tool marker with message - match by runId if available
          let markerIndex = -1;
          if (runId) {
            // Prefer matching by runId for precise identification
            markerIndex = toolMarkers.findIndex(m => m.runId === runId);
          } else {
            // Fallback: find the most recent incomplete one for this tool
            // Look for markers with null success status (in progress)
            for (let i = toolMarkers.length - 1; i >= 0; i--) {
              if (toolMarkers[i].tool === toolName && (toolMarkers[i].success === null || toolMarkers[i].success === undefined)) {
                markerIndex = i;
                break;
              }
            }
          }
          
          if (markerIndex !== -1) {
            toolMarkers[markerIndex] = { 
              ...toolMarkers[markerIndex], 
              message: toolMessage,
              success: toolSuccess
            };
          } else {
            // If no matching marker found, add a new one at current position
            const position = fullText.length;
            toolMarkers.push({ position, tool: toolName, runId, message: toolMessage, success: toolSuccess });
          }
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 70,
            currentActivity: `Completed: ${toolName}`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
            executionTodo: executionTodo || undefined,
          });
        } else if (chunk.type === 'tools_node_end') {
          toolsNodeCompleted = true;
          // Mark all incomplete tools as complete when tools node ends
          toolMarkers.forEach((marker, index) => {
            if (!marker.message) {
              toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
            } else if (marker.success === null || marker.success === undefined) {
              // Catch synthetic markers (e.g. video_processing) that have a message but no success
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 70,
            currentActivity: 'Tools completed',
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
          });
        } else if (chunk.type === 'agent_cycle_start') {
          const description = chunk.description || '';
          // If this is the final response after tools, mark incomplete tools as complete
          if (description.includes('final response') && toolsNodeCompleted) {
            finalResponseStarted = true;
            toolMarkers.forEach((marker, index) => {
              if (!marker.message) {
                toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
              } else if (marker.success === null || marker.success === undefined) {
                toolMarkers[index] = { ...marker, success: true };
              }
            });
            onProgress?.({
              requestId,
              stage: ResponseStage.STREAMING,
              progress: 75,
              currentActivity: 'Final response starting',
              startTime,
              estimatedDuration: 30000,
              canCancel: true,
              activeTools: Array.from(activeTools),
              toolMarkers: [...toolMarkers],
              streamedContent: fullText,
            });
          }
        } else if (chunk.type === 'structured_questions') {
          // Agent paused to present a structured questionnaire.
          // Emit as final response metadata so the store can render the
          // questionnaire wizard and pause waiting for user answers.
          console.log('[chatbotApi] Structured questions received:', chunk.questions?.length, 'questions');
          // Mark all in-progress tools as complete (the interrupt pauses before tool_end fires)
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
          return {
            id: `response_${Date.now()}`,
            content: fullText || '',
            timestamp: Date.now(),
            conversationId,
            metadata: {
              requires_questionnaire: true,
              questionnaire_data: {
                questions: chunk.questions || [],
                conversation_id: chunk.conversation_id || conversationId,
              },
            },
          };
        } else if (chunk.type === 'graph_complete') {
          // Ensure all incomplete tools are marked as complete
          toolMarkers.forEach((marker, index) => {
            if (!marker.message) {
              toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
            } else if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          
          // Send final update with idle status and all tool markers
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
        } else if (chunk.type === 'response_reset') {
          // Worker died mid-stream and the backend is auto-recovering.
          // Clear all partial state so the resumed stream starts clean.
          fullText = '';
          toolMarkers.length = 0;
          outputTokenCount = 0;
          toolResponseTokens = 0;
          toolsNodeCompleted = false;
          finalResponseStarted = false;
          activeTools.clear();

          // Phase 2: Reset lastEventId so reconnection after recovery doesn't
          // re-request old events from the prior attempt_id. The new worker
          // attempt starts a fresh stream with sequence 0.
          lastEventId = 0;
          const resetStore = useChatbotStore.getState();
          if (resetStore.conversationId === conversationId) {
            resetStore.updateSSEConnectionState({ lastEventId: 0 });
          }

          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 30,
            currentActivity: 'Recovering...',
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: '',
            toolMarkers: [],
            activeTools: [],
            isStreamReset: true,
          });
        } else if (chunk.type === 'status') {
          // Status update from backend (e.g., video processing progress)
          const statusMessage = chunk.message || 'Processing...';
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 50,
            currentActivity: statusMessage,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
          });
        } else if (chunk.type === 'overflow_recovery_started') {
          // Context overflow detected - recovery is starting
          // This will take ~35 seconds while Gemini generates a summary
          console.log('🔄 [chatbotApi] Overflow recovery started event received:', {
            chunk,
            timestamp: new Date().toISOString(),
            currentConversationId: conversationId,
          });
          
          const store = useChatbotStore.getState();
          store.handleOverflowRecoveryStarted(chunk.original_conversation_id || conversationId);
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 50,
            currentActivity: chunk.message || 'Creating a summary of our conversation...',
            startTime,
            estimatedDuration: 40000, // ~40 seconds for summary
            canCancel: false,
            streamedContent: fullText,
          });
        } else if (chunk.type === 'continuation_complete') {
          // Continuation conversation created successfully
          // Frontend should switch to new conversation and auto-retry
          console.log('✅ [chatbotApi] Continuation complete event received:', {
            newConversationId: chunk.conversation_id,
            retryMessagePreview: chunk.retry_message?.slice(0, 50) + '...',
            continuedFrom: chunk.continued_from,
            timestamp: new Date().toISOString(),
          });
          
          const store = useChatbotStore.getState();
          await store.handleContinuationComplete(
            chunk.conversation_id,
            chunk.retry_message,
            chunk.continued_from,
            chunk.continuation_chain || []
          );
          
          console.log('✅ [chatbotApi] handleContinuationComplete finished, returning early');
          
          // Return early - the retry will happen in the new conversation
          // Mark as continuation so _handleRealResponse knows not to add this message
          return {
            id: `continuation_${Date.now()}`,
            content: chunk.message || 'Conversation continued in a new thread.',
            timestamp: Date.now(),
            conversationId: chunk.conversation_id,
            metadata: {
              isContinuation: true, // Flag to skip adding this message in _handleRealResponse
            },
          };
        } else if (chunk.type === 'stream_interrupted') {
          // Stream was interrupted mid-response.
          // IMPORTANT UX: if the user explicitly clicked Stop, do not show the interruption banner.
          if (isStoppedByUserStreamInterruption(chunk)) {
            console.log('🛑 [chatbotApi] Stream cancelled by user (backend acknowledged).');
            finalizeInProgressToolMarkers(toolMarkers); // stop any tool spinners immediately
            activeTools.clear();
            useChatbotStore.getState().setActiveTools([]);

            onProgress?.({
              requestId,
              stage: ResponseStage.IDLE,
              progress: 100,
              currentActivity: 'Stopped',
              startTime,
              estimatedDuration: 0,
              canCancel: false,
              streamedContent: chunk.partial_response || fullText,
              toolMarkers: [...toolMarkers],
              activeTools: [],
            });

            return {
              id: `cancelled_${Date.now()}`,
              content: chunk.partial_response || fullText,
              timestamp: Date.now(),
              conversationId,
              metadata: {
                isInterrupted: true,
                error: chunk.error,
                isRetryable: false,
              },
            };
          }

          // Otherwise, surface to frontend for user-initiated retry
          console.error('❌ [chatbotApi] Stream interrupted:', {
            partialResponseLength: chunk.partial_response?.length || 0,
            error: chunk.error,
            isRetryable: chunk.is_retryable,
          });
          captureChatWarning('SSE stream_interrupted event', {
            error: chunk.error,
            isRetryable: chunk.is_retryable,
            partialResponseLength: chunk.partial_response?.length || 0,
            conversationId,
          });
          
          const store = useChatbotStore.getState();
          store.handleStreamInterrupted(
            chunk.partial_response || fullText,
            chunk.error || 'Unknown error',
            chunk.is_retryable !== false, // Default to true
            message, // Original user message
            uploadedImages
          );
          
          // Return partial response so it can be displayed
          return {
            id: `interrupted_${Date.now()}`,
            content: chunk.partial_response || fullText,
            timestamp: Date.now(),
            conversationId,
            metadata: {
              isInterrupted: true,
              error: chunk.error,
              isRetryable: chunk.is_retryable,
            },
          };
        } else if (chunk.type === 'error') {
          // Check if this is an unrecoverable overflow error
          if (chunk.error_type === 'overflow_unrecoverable' || chunk.requires_new_conversation) {
            console.error('❌ [chatbotApi] Unrecoverable overflow error:', chunk);
            
            const store = useChatbotStore.getState();
            store.handleOverflowError(chunk.content || 'This conversation has become too complex. Please start a new conversation.');
            
            // Return the error response instead of throwing
            return {
              id: `error_${Date.now()}`,
              content: chunk.content || 'Conversation overflow - please start a new conversation.',
              timestamp: Date.now(),
              conversationId,
            };
          }
          
          // Finalize any pending tool spinners before throwing
          finalizeInProgressToolMarkers(toolMarkers);
          activeTools.clear();
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Error',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
          throw new Error(chunk.content || 'Streaming error');
        }
          }
          
          // Stream completed successfully
          streamComplete = true;

          // Safety net: if stream ended without graph_complete, finalize any
          // tool markers still showing as in-progress (success === null).
          // This handles network drops, server crashes, or missing graph_complete.
          if (toolMarkers.some(m => m.success === null || m.success === undefined)) {
            finalizeInProgressToolMarkers(toolMarkers);
            onProgress?.({
              requestId,
              stage: ResponseStage.IDLE,
              progress: 100,
              currentActivity: 'Complete',
              startTime,
              estimatedDuration: 0,
              canCancel: false,
              streamedContent: fullText,
              toolMarkers: [...toolMarkers],
              activeTools: [],
            });
            activeTools.clear();
          }

        } catch (streamError) {
          // Check if this is an abort (user cancelled)
          if (streamError instanceof Error && streamError.name === 'AbortError') {
            // Don't retry aborts - rethrow to outer catch
            throw streamError;
          }

          // Check if this is a network/connection error that we can retry
          const isNetworkError = streamError instanceof TypeError ||
            (streamError instanceof Error && (
              streamError.message.includes('network') ||
              streamError.message.includes('fetch') ||
              streamError.message.includes('Failed to fetch') ||
              streamError.message.includes('NetworkError') ||
              streamError.message.includes('connection')
            ));
          
          const reconnectElapsedMs = Date.now() - reconnectStart;
          if (isNetworkError && reconnectAttempts < MAX_RECONNECT_ATTEMPTS && reconnectElapsedMs < MAX_RECONNECT_WINDOW_MS) {
            reconnectAttempts++;
            if (!hadNetworkDisconnect) {
              const store = useChatbotStore.getState();
              if (store.conversationId === conversationId) {
                store.handleSSEDisconnect();
                store.updateSSEConnectionState({ lastEventId });
              }
              hadNetworkDisconnect = true;
            }
            const store = useChatbotStore.getState();
            if (store.conversationId === conversationId) {
              store.updateStreamInterruptedState({ lastReconnectAttemptAt: Date.now() });
            }
            console.log(`🔄 [chatbotApi] Connection lost, attempting reconnection ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} (lastEventId=${lastEventId || 0})`);
            
            // Show reconnection feedback to user
            onProgress?.({
              requestId,
              stage: ResponseStage.WAITING,
              progress: 50,
              currentActivity: `Reconnecting... (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
              startTime,
              estimatedDuration: 30000,
              canCancel: true,
              streamedContent: fullText,
              toolMarkers: [...toolMarkers],
              activeTools: Array.from(activeTools),
            });
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY_MS * reconnectAttempts));
            
            // Continue to next iteration of while loop to retry
            continue;
          }
          
          // Non-recoverable error or max retries exceeded
          throw streamError;
        }
      }

      // Clear abort controller on successful completion
      this.abortController = null;

      // After stream completes, return final response with all tool markers
      // Note: suggestedResponses will be parsed from fullText on the frontend
      return {
        id: `response_${Date.now()}`,
        content: fullText, // Contains raw XML, will be parsed on frontend
        timestamp: Date.now(),
        conversationId,
      };
    } catch (error) {
      // Handle user-initiated abort
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[chatbotApi] Stream aborted by user');
        finalizeInProgressToolMarkers(toolMarkers); // stop any tool spinners immediately
        this.abortController = null;
        useChatbotStore.getState().setActiveTools([]);

        onProgress?.({
          requestId,
          stage: ResponseStage.IDLE,
          progress: 100,
          currentActivity: 'Stopped',
          startTime,
          estimatedDuration: 0,
          canCancel: false,
          streamedContent: fullText,
          toolMarkers: [...toolMarkers],
          activeTools: [],
        });
        // Return partial content that was streamed
        return {
          id: `response_${Date.now()}`,
          content: fullText,
          timestamp: Date.now(),
          conversationId,
          metadata: {
            isInterrupted: true,
          },
        };
      }

      // Finalize any stuck tool spinners on non-abort errors
      finalizeInProgressToolMarkers(toolMarkers);
      useChatbotStore.getState().setActiveTools([]);
      onProgress?.({
        requestId,
        stage: ResponseStage.IDLE,
        progress: 100,
        currentActivity: 'Error',
        startTime,
        estimatedDuration: 0,
        canCancel: false,
        streamedContent: fullText,
        toolMarkers: [...toolMarkers],
        activeTools: [],
      });

      this.abortController = null;
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Streaming failed');
    }
  }

  /**
   * Cancel the current request
   */
  cancelRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }


  /**
   * Submit approval for a pending task
   */
  async submitApproval(
    conversationId: string,
    approvalData: any,
    approved: boolean = true
  ): Promise<ChatbotApiResponse> {
    const approvalRequest = {
      message: 'approve',
      conversation_id: conversationId,
      is_approval: true,
      approved: approved,
      approval_data: approvalData,
    };

    try {
      const result = await NewConversationService.approve(conversationId, approvalRequest);

      // Extract response message
      let assistantResponse = result.response;
      if (typeof assistantResponse === 'string') {
        try {
          const parsedResponse = JSON.parse(assistantResponse);
          assistantResponse = parsedResponse.message || parsedResponse.answer || assistantResponse;
        } catch (error) {
          // Use original if parsing fails
        }
      }

      return {
        id: result.conversation_id ?? `response_${Date.now()}`,
        content: assistantResponse || 'Task approved successfully.',
        timestamp: Date.now(),
        conversationId: result.conversation_id,
      };
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : 'Failed to submit approval'
      );
    }
  }

  /**
   * Submit questionnaire answers and stream the agent's continuation.
   * Uses the same SSE streaming pattern as sendMessageStream.
   */
  async submitQuestionnaireAnswersStream(
    conversationId: string,
    answers: Array<{ question_id: string; selected_option: string | null; freeform_text: string | null }>,
    onProgress?: ProgressCallback,
  ): Promise<ChatbotApiResponse> {
    const startTime = Date.now();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    let fullText = '';
    let outputTokenCount = 0;
    const activeTools = new Set<string>();
    const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];

    this.abortController = new AbortController();

    try {
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 10,
        currentActivity: 'Leo is processing your answers...',
        startTime,
        estimatedDuration: 30000,
        canCancel: false,
        streamedContent: '',
        toolMarkers: [],
      });

      for await (const { data: chunk } of NewConversationService.streamQuestionnaire(
        conversationId,
        answers,
        this.abortController?.signal,
      )) {
        if (chunk.type === 'keepalive') {
          continue;
        } else if (chunk.type === 'token') {
          fullText += chunk.content || '';
          outputTokenCount++;

          onProgress?.({
            requestId,
            stage: ResponseStage.STREAMING,
            progress: 75,
            currentActivity: `Streaming...`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
          });
        } else if (chunk.type === 'tool_start') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `using ${toolName}`;
          activeTools.add(toolName);
          toolMarkers.push({
            position: fullText.length,
            tool: toolName,
            runId: chunk.run_id,
            message: toolMessage,
            success: null,
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.TOOL_CALLING,
            progress: 50,
            currentActivity: toolMessage,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
          });
        } else if (chunk.type === 'tool_end') {
          const toolName = chunk.tool || 'unknown';
          activeTools.delete(toolName);
          const markerIdx = toolMarkers.findIndex(m => m.runId === chunk.run_id || m.tool === toolName);
          if (markerIdx !== -1) {
            toolMarkers[markerIdx] = {
              ...toolMarkers[markerIdx],
              message: chunk.message || toolMarkers[markerIdx].message,
              success: chunk.success === true ? true : chunk.success === false ? false : null,
            };
          }
          // Extract execution todo from tool_end (mirrors sendMessageStream logic)
          const executionTodo = extractExecutionTodoFromStreamChunk(chunk);
          if (executionTodo) {
            const store = useChatbotStore.getState();
            if (store.conversationId === conversationId) {
              store.setExecutionTodo(executionTodo);
            }
          }
        } else if (chunk.type === 'structured_questions') {
          // Chained questionnaire — the agent called ask_structured_questions
          // again during the resume (e.g., rundown nudge triage after transition).
          // Handle identically to sendMessageStream: mark tools complete, return
          // with questionnaire metadata so the store renders the next wizard.
          console.log('[chatbotApi] Chained structured questions received:', chunk.questions?.length, 'questions');
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });

          this.abortController = null;
          return {
            id: `response_${Date.now()}`,
            content: fullText || '',
            timestamp: Date.now(),
            conversationId,
            metadata: {
              requires_questionnaire: true,
              questionnaire_data: {
                questions: chunk.questions || [],
                conversation_id: chunk.conversation_id || conversationId,
              },
            },
          };
        } else if (chunk.type === 'graph_complete') {
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
        } else if (chunk.type === 'error') {
          throw new Error(chunk.content || 'Streaming error');
        }
      }

      this.abortController = null;

      return {
        id: `response_${Date.now()}`,
        content: fullText,
        timestamp: Date.now(),
        conversationId,
      };
    } catch (error) {
      this.abortController = null;
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Questionnaire streaming failed');
    }
  }

  /**
   * Get chatbot health status (using new conversation endpoint)
   */
  async getHealthStatus(): Promise<{ status: 'healthy' | 'degraded' | 'down'; message?: string }> {
    try {
      // Test the new-conversations endpoint to check health
      await NewConversationService.getAll();
      return { status: 'healthy' };
    } catch (error) {
      return { 
        status: 'down', 
        message: error instanceof Error ? error.message : 'Network error' 
      };
    }
  }

}

// Export singleton instance
export const chatbotApi = new ChatbotApiService();
export default chatbotApi;